import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ActivityStats, ActivityCompletion } from '../types/assessment.types';

// Query key factory — follows established pattern from useAssessment.ts
export const activityStatsKeys = {
  all: ['activityStats'] as const,
  detail: (childId: number) => [...activityStatsKeys.all, childId] as const,
};

/**
 * Fetch aggregated activity stats for a child (this week, this month, this year).
 * Returns { thisWeek, thisMonth, thisYear } counts of unique activities completed.
 */
export function useActivityStats(childId: number, enabled = true) {
  return useQuery<ActivityStats>({
    queryKey: activityStatsKeys.detail(childId),
    queryFn: () => api.getActivityStats(),
    enabled: enabled && childId > 0,
  });
}

/**
 * Record a child completing an activity.
 * Invalidates activity stats cache so the Sparks History card updates.
 */
export function useCompleteActivity() {
  const queryClient = useQueryClient();

  return useMutation<
    ActivityCompletion,
    Error,
    { childId: number; activityId: number }
  >({
    mutationFn: ({ childId, activityId }) =>
      api.completeActivity(childId, activityId),
    onSuccess: (_data, variables) => {
      // Invalidate this child's stats so the Sparks History card refreshes
      queryClient.invalidateQueries({
        queryKey: activityStatsKeys.detail(variables.childId),
      });
    },
  });
}
